/*
 * 商品ページ「こちらもおすすめ」レコメンド（EC-392）
 *
 * 表示中の商品と同じVendor（メーカー）/ 同じProduct Type（カテゴリ）の商品を
 * /search で優先取得する。ヒットが0件、またはVendor/Typeが両方空の場合のみ、
 * 従来の Product Recommendations API（related）にフォールバックする。
 *
 * Vendor/Type一致は関連度順で複数返るため、表示件数を超える候補があるときは
 * クライアント側でランダムに抽選する（毎回同じ上位N件にならないように）。
 * 逆に候補が表示件数に満たないときは、残り枠を Product Recommendations
 * （related）で埋める。
 *
 * カート内クロスセル（assets/cart-recommendations.js）と同じ方式。
 * グローバルな <product-recommendations>（assets/global.js、補完商品ブロック等でも
 * 共用）とは別タグ・別クラスとして定義し、影響範囲をこのセクションに限定する。
 */
if (!customElements.get('related-products-recommend')) {
  class RelatedProductsRecommend extends HTMLElement {
    connectedCallback() {
      // バンドル逆引きカード（EC-392）: フェッチで innerHTML が丸ごと差し替わる前に、
      // 初回レンダリング時点で埋め込まれた <template data-bundle-template> の中身を
      // 読み取って保持しておく（フェッチ先の /search 等には product オブジェクトが
      // 無く、このカードは再現できないため）。
      const bundleTemplate = this.querySelector('template[data-bundle-template]');
      this.bundleItems = bundleTemplate
        ? Array.from(bundleTemplate.content.querySelectorAll('[data-bundle-item]')).map((node) => node.cloneNode(true))
        : [];

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          this.loadRecommendations();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    loadRecommendations() {
      const vendor = this.dataset.vendor || '';
      const type = this.dataset.type || '';

      if (!vendor && !type) {
        this.loadByRecommendationsApi();
        return;
      }

      this.loadByVendorOrType(vendor, type);
    }

    // 同じVendor / 同じProduct Type の商品を /search で検索する。
    loadByVendorOrType(vendor, type) {
      const productId = this.dataset.productId;
      const clauses = [];
      if (vendor) clauses.push(`(vendor:"${vendor}")`);
      if (type) clauses.push(`(product_type:"${type}")`);
      const q = encodeURIComponent(clauses.join(' OR '));
      const url = `/search?q=${q}&type=product&options[unavailable_products]=hide&section_id=${this.dataset.sectionId}`;

      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          this.removeCurrentProductCard(html);

          const grid = html.querySelector('.grid');
          const items = grid ? Array.from(grid.querySelectorAll('.grid__item')) : [];

          if (items.length === 0) {
            // Vendor/Type検索が空振りならProduct Recommendationsにフォールバック
            this.loadByRecommendationsApi();
            return;
          }

          const limit = this.getLimit(items.length);
          this.pickRandom(items, limit, grid);

          if (items.length < limit) {
            this.fillRemainingSlots(html, grid, items, limit);
            return;
          }

          this.reveal(html);
        })
        .catch((e) => {
          console.error(e);
          this.loadByRecommendationsApi();
        });
    }

    getLimit(fallbackCount) {
      const parsed = parseInt(this.dataset.limit || '', 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallbackCount;
    }

    // items（候補一覧、grid内のDOM要素）が limit を超える場合、ランダムに
    // limit件だけ残して他をDOMから取り除く（Fisher-Yatesシャッフル）。
    pickRandom(items, limit, grid) {
      if (items.length <= limit) return;

      for (let i = items.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
      }

      const dropped = items.splice(limit);
      dropped.forEach((item) => grid?.removeChild(item));
    }

    // Vendor/Type 一致が表示件数に満たないとき、Product Recommendations
    // （related）で残り枠を埋める。既出商品（Vendor/Type一致・表示中の商品自身）
    // は除外する。
    fillRemainingSlots(html, grid, existingItems, limit) {
      const productId = this.dataset.productId;
      fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
        .then((response) => response.text())
        .then((text) => {
          const recHtml = document.createElement('div');
          recHtml.innerHTML = text;
          const candidates = Array.from(recHtml.querySelectorAll('.grid__item'));

          const seenIds = new Set(existingItems.map((item) => item.dataset.productId));
          seenIds.add(productId);

          for (const candidate of candidates) {
            if (existingItems.length >= limit) break;
            const id = candidate.dataset.productId;
            if (!id || seenIds.has(id)) continue;
            seenIds.add(id);
            grid?.appendChild(candidate);
            existingItems.push(candidate);
          }

          this.reveal(html);
        })
        .catch((e) => {
          console.error(e);
          this.reveal(html);
        });
    }

    // 表示中の商品自身のカードを、取得したHTML断片から取り除く。
    removeCurrentProductCard(html) {
      const currentId = this.dataset.productId;
      html.querySelectorAll(`[data-product-id="${currentId}"]`).forEach((item) => item.remove());
    }

    // 従来の Product Recommendations API（related）
    loadByRecommendationsApi() {
      const productId = this.dataset.productId;
      fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          this.reveal(html);
        })
        .catch((e) => {
          console.error(e);
        });
    }

    reveal(html) {
      const recommendations = html.querySelector('related-products-recommend');
      if (recommendations) {
        this.mergeBundleItems(recommendations);
      }
      if (recommendations?.innerHTML.trim().length) {
        this.innerHTML = recommendations.innerHTML;
      }
      if (html.querySelector('.grid__item')) {
        this.classList.add('product-recommendations--loaded');
      }
    }

    // バンドル逆引きカード（EC-392）: connectedCallback で保持した this.bundleItems
    // （現在の商品が構成品であるバンドル商品のカード）を、フェッチしてきた結果の
    // グリッド先頭にマージする。表示中の商品自身と重複するIDは除外し、表示件数
    // （data-limit）を超える分は末尾（＝優先度が低い候補）からトリムする。
    mergeBundleItems(recommendations) {
      if (!this.bundleItems || this.bundleItems.length === 0) return;

      const grid = recommendations.querySelector('.grid');
      if (!grid) return;

      const existingItems = Array.from(grid.querySelectorAll('.grid__item'));
      const existingIds = new Set(existingItems.map((item) => item.dataset.productId));
      const toInsert = this.bundleItems.filter((item) => !existingIds.has(item.dataset.productId));

      toInsert
        .slice()
        .reverse()
        .forEach((item) => grid.insertBefore(item.cloneNode(true), grid.firstChild));

      const limit = this.getLimit(existingItems.length + toInsert.length);
      const allItems = Array.from(grid.querySelectorAll('.grid__item'));
      if (allItems.length > limit) {
        allItems.slice(limit).forEach((item) => grid.removeChild(item));
      }
    }
  }

  customElements.define('related-products-recommend', RelatedProductsRecommend);
}
